// ============================================================================
// CHAT INTERNO DA EQUIPE — cliente HTTP
// ============================================================================

import { api } from './api';

export type TeamChatConversationType = 'direct' | 'group';
export type TeamChatParticipantRole = 'owner' | 'admin' | 'member';
export type TeamChatMessageType = 'text' | 'image' | 'file' | 'audio' | 'system';

export interface TeamChatUnitRef {
  id: string;
  name: string;
  short_name: string;
}

export interface TeamChatDirectoryUser {
  id: string;
  full_name: string;
  email: string;
  role: string;
  avatar_url: string | null;
  gender: string | null;
  is_active: boolean;
  units: TeamChatUnitRef[];
  online?: boolean;
}

export interface TeamChatParticipant {
  user_id: string;
  role: TeamChatParticipantRole;
  last_read_at: string | null;
  full_name: string;
  avatar_url: string | null;
  gender: string | null;
  system_role: string | null;
  units: TeamChatUnitRef[];
  online: boolean;
}

export interface TeamChatConversation {
  id: string;
  type: TeamChatConversationType;
  title: string;
  description: string | null;
  avatar_url: string | null;
  counterpart: TeamChatParticipant | null;
  unit_id: string | null;
  created_by: string | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_sender_id: string | null;
  message_count: number;
  participants: TeamChatParticipant[];
  participant_count: number;
  my_role: TeamChatParticipantRole;
  my_last_read_at: string | null;
  is_pinned: boolean;
  is_muted: boolean;
  muted_until: string | null;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface TeamChatMessageSender {
  id: string;
  full_name: string;
  avatar_url: string | null;
  gender: string | null;
  role: string | null;
}

export interface TeamChatMessageReply {
  id: string;
  body: string | null;
  message_type: TeamChatMessageType;
  attachment_name: string | null;
  is_deleted: boolean;
  sender_name: string | null;
}

export interface TeamChatReaction {
  emoji: string;
  user_ids: string[];
}

export interface TeamChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string | null;
  body: string | null;
  message_type: TeamChatMessageType;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_mime: string | null;
  attachment_size: number | null;
  reply_to_id: string | null;
  mentions: string[];
  is_edited: boolean;
  edited_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  created_at: string;
  sender: TeamChatMessageSender | null;
  reply_to: TeamChatMessageReply | null;
  reactions: TeamChatReaction[];
}

export interface TeamChatMessagePage {
  messages: TeamChatMessage[];
  has_more: boolean;
}

export interface TeamChatUnreadSummary {
  total: number;
  byConversation: Record<string, number>;
}

export interface SendTeamChatMessageInput {
  body?: string | null;
  reply_to_id?: string | null;
  mentions?: string[];
}

class TeamChatService {
  async getDirectory(search?: string): Promise<TeamChatDirectoryUser[]> {
    const res = await api.get('/team-chat/directory', { params: search ? { search } : undefined });
    return res.data?.data || [];
  }

  async getOnlineUserIds(): Promise<string[]> {
    const res = await api.get('/team-chat/presence');
    return res.data?.data?.user_ids || [];
  }

  async getConversations(): Promise<TeamChatConversation[]> {
    const res = await api.get('/team-chat/conversations');
    return res.data?.data || [];
  }

  async getConversation(conversationId: string): Promise<TeamChatConversation> {
    const res = await api.get(`/team-chat/conversations/${conversationId}`);
    return res.data?.data;
  }

  async getUnreadSummary(): Promise<TeamChatUnreadSummary> {
    const res = await api.get('/team-chat/unread-count', {
      params: { _ts: Date.now() },
      headers: { 'Cache-Control': 'no-cache' },
    });
    return res.data?.data || { total: 0, byConversation: {} };
  }

  async openDirect(userId: string): Promise<TeamChatConversation> {
    const res = await api.post('/team-chat/conversations/direct', { user_id: userId });
    return res.data?.data;
  }

  async createGroup(payload: {
    title: string;
    description?: string;
    participant_ids: string[];
    unit_id?: string | null;
  }): Promise<TeamChatConversation> {
    const res = await api.post('/team-chat/conversations/group', payload);
    return res.data?.data;
  }

  async updateGroup(
    conversationId: string,
    payload: { title?: string; description?: string | null },
  ): Promise<TeamChatConversation> {
    const res = await api.patch(`/team-chat/conversations/${conversationId}`, payload);
    return res.data?.data;
  }

  async updatePreferences(
    conversationId: string,
    payload: { is_pinned?: boolean; is_muted?: boolean },
  ): Promise<TeamChatConversation> {
    const res = await api.patch(`/team-chat/conversations/${conversationId}/preferences`, payload);
    return res.data?.data;
  }

  async addParticipants(conversationId: string, userIds: string[]): Promise<TeamChatConversation> {
    const res = await api.post(`/team-chat/conversations/${conversationId}/participants`, {
      user_ids: userIds,
    });
    return res.data?.data;
  }

  async removeParticipant(conversationId: string, userId: string): Promise<void> {
    await api.delete(`/team-chat/conversations/${conversationId}/participants/${userId}`);
  }

  async getMessages(
    conversationId: string,
    opts: { before?: string; limit?: number } = {},
  ): Promise<TeamChatMessagePage> {
    const res = await api.get(`/team-chat/conversations/${conversationId}/messages`, { params: opts });
    return res.data?.data || { messages: [], has_more: false };
  }

  async sendMessage(
    conversationId: string,
    payload: SendTeamChatMessageInput,
  ): Promise<TeamChatMessage> {
    const res = await api.post(`/team-chat/conversations/${conversationId}/messages`, payload);
    return res.data?.data;
  }

  async sendAttachment(
    conversationId: string,
    file: File,
    opts: { body?: string; replyToId?: string | null } = {},
  ): Promise<TeamChatMessage> {
    const form = new FormData();
    form.append('file', file);
    if (opts.body) form.append('body', opts.body);
    if (opts.replyToId) form.append('reply_to_id', opts.replyToId);

    const res = await api.post(`/team-chat/conversations/${conversationId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return res.data?.data;
  }

  async editMessage(messageId: string, body: string): Promise<TeamChatMessage> {
    const res = await api.patch(`/team-chat/messages/${messageId}`, { body });
    return res.data?.data;
  }

  async deleteMessage(messageId: string): Promise<TeamChatMessage> {
    const res = await api.delete(`/team-chat/messages/${messageId}`);
    return res.data?.data;
  }

  async toggleReaction(messageId: string, emoji: string): Promise<TeamChatMessage> {
    const res = await api.post(`/team-chat/messages/${messageId}/reactions`, { emoji });
    return res.data?.data;
  }

  async markRead(conversationId: string): Promise<void> {
    await api.post(`/team-chat/conversations/${conversationId}/read`, {});
  }

  async search(term: string): Promise<TeamChatMessage[]> {
    const res = await api.get('/team-chat/search', { params: { q: term } });
    return res.data?.data || [];
  }
}

export const teamChatService = new TeamChatService();
export default teamChatService;
